/**
 * Frontend Sentry setup (PROD-8, PROD-12).
 *
 * - `initSentry()` is called once from app/_layout.tsx.
 * - `reportHandledError()` is for errors we catch on purpose but still want
 *   to see. It reports at level `warning` with metadata only (GDPR).
 */
import * as Sentry from '@sentry/react-native';

/**
 * Messages that are browser noise, not app bugs. Sentry's built-in list
 * (inboundFilters DEFAULT_IGNORE_ERRORS) already drops "Script error." and
 * "ResizeObserver loop completed with undelivered notifications.", so only
 * add what it misses.
 */
export const SENTRY_IGNORE_ERRORS: RegExp[] = [
  // Older Chromium/Safari wording of the same benign ResizeObserver warning
  // (react-native-web measures layout with ResizeObserver). Not in the
  // default list.
  /^ResizeObserver loop limit exceeded$/,
  // fontfaceobserver timeout from expo-font's web loader (12000 ms), raised
  // when @expo/vector-icons loads its icon font in componentDidMount with no
  // catch (PROD-8, Sentry FRONTEND-2Q). App code cannot catch it: expo-font
  // shares one load promise per font family, so a preload with .catch() would
  // not stop the icon component's own await from rejecting. The page keeps
  // working; at worst an icon renders late.
  /^\d+ms timeout exceeded$/,
];

/**
 * Errors whose top stack frame is in a browser extension come from code we
 * do not ship. Filtering by frame URL is narrower than guessing at messages.
 */
export const SENTRY_DENY_URLS: RegExp[] = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-(web-)?extension:\/\//,
];

export function initSentry(): void {
  if (process.env.EXPO_PUBLIC_SENTRY_DSN) {
    Sentry.init({
      dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
      environment: process.env.EXPO_PUBLIC_SENTRY_ENV || 'development',
      tracesSampleRate: parseFloat(process.env.EXPO_PUBLIC_SENTRY_TRACES_SAMPLE_RATE || '0'),
      enableAutoPerformanceTracing: true,
      debug: false,
      enableNative: true,
      ignoreErrors: SENTRY_IGNORE_ERRORS,
      denyUrls: SENTRY_DENY_URLS,
    });
  } else {
    // EXPO_PUBLIC_* vars are inlined at build time (`npx expo export`), so a
    // missing DSN here means it wasn't set as a build arg when this bundle was
    // built (see frontend/Dockerfile ARG/ENV EXPO_PUBLIC_SENTRY_DSN) — not a
    // runtime config issue. Surfaces silently-disabled error reporting in the
    // browser console instead of a permanent, undetectable no-op.
    // eslint-disable-next-line no-console
    console.warn(
      '[Sentry] EXPO_PUBLIC_SENTRY_DSN was not set at build time — error reporting is disabled for this build.'
    );
  }
}

/** Metadata about where a handled error happened. Never user content. */
export interface HandledErrorContext {
  /** Stable identifier of the catch site, e.g. 'auth.sso_exchange'. */
  source: string;
  [key: string]: string | number | boolean | null | undefined;
}

const MAX_CONTEXT_STRING = 100;

/**
 * Keep only primitive metadata: drop objects/arrays (they could carry user
 * content) and truncate strings.
 */
export function sanitizeHandledErrorContext(context: HandledErrorContext): Record<string, string | number | boolean> {
  const safe: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(context)) {
    if (typeof value === 'string') safe[key] = value.slice(0, MAX_CONTEXT_STRING);
    else if (typeof value === 'number' || typeof value === 'boolean') safe[key] = value;
  }
  return safe;
}

function httpStatusOf(error: unknown): number | undefined {
  const e = error as { response?: { status?: unknown }; status?: unknown } | null;
  const status = e?.response?.status ?? e?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Report an error that was caught on purpose (the UI carries on) so it is
 * still visible in Sentry at level `warning`. Never throws.
 */
export function reportHandledError(error: unknown, context: HandledErrorContext): void {
  try {
    const extra: Record<string, string | number | boolean> = sanitizeHandledErrorContext(context);
    const status = httpStatusOf(error);
    if (status !== undefined && extra.status === undefined) extra.status = status;

    // A thrown non-Error (string, object) may be user content: report only its type.
    let reported: Error;
    if (error instanceof Error) {
      reported = error;
    } else {
      reported = new Error('non_error_thrown');
      extra.error_type = error === null ? 'null' : typeof error;
    }

    Sentry.captureException(reported, {
      level: 'warning',
      tags: { handled: 'true', source: extra.source as string },
      extra,
    });
  } catch {
    // Reporting must never break the caller.
  }
}
